import { Input } from 'components/custom/input';
import { useTranslate } from 'context/TranslateContext';
import { removeErrorOnChange } from 'helpers/functions/removeErrorOnChange';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { validate } from './validator';

export interface IPWhitelistModalForm {
  ip_address: string;
  description?: string;
  is_enabled: boolean;
}

interface IPWhitelistModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialValue?: IPWhitelistModalForm | null;
  onClose: () => void;
  onSuccess: () => void;
  onSubmit: (form: IPWhitelistModalForm) => Promise<boolean>;
}

const defaultForm: IPWhitelistModalForm = {
  ip_address: '',
  description: '',
  is_enabled: true,
};

export const IPWhitelistModal = ({
  isOpen,
  mode,
  initialValue,
  onClose,
  onSuccess,
  onSubmit,
}: IPWhitelistModalProps) => {
  const { translate } = useTranslate();
  const [form, setForm] = useState<IPWhitelistModalForm>(defaultForm);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setForm(
      initialValue
        ? {
            ip_address: initialValue.ip_address,
            description: initialValue.description ?? '',
            is_enabled: initialValue.is_enabled,
          }
        : defaultForm,
    );
    setErrors({});
    setIsSubmitting(false);

    return () => {
      setIsSubmitting(false);
    };
  }, [initialValue, mode]);

  const modalTitleKey = mode === 'create' ? 'ip_whitelist_create_title' : 'ip_whitelist_edit_title';
  const modalTitle = translate(modalTitleKey);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setErrors((prev) => removeErrorOnChange(prev, e.target.name));
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEnabledCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      is_enabled: e.target.checked,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload: IPWhitelistModalForm = {
      ip_address: form.ip_address.trim(),
      description: form.description?.trim() ?? '',
      is_enabled: form.is_enabled,
    };

    const { errors: validationErrors, isValid } = validate(payload);
    setErrors(validationErrors);
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    const success = await onSubmit({
      ...payload,
      description: payload.description || undefined,
    });
    if (!success) {
      setIsSubmitting(false);
      return;
    }
    onSuccess();
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} backdrop="static">
      <ModalHeader toggle={onClose}>{modalTitle}</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <Input
            legend={translate('ip_whitelist_cidr').toUpperCase()}
            name="ip_address"
            value={form.ip_address}
            onChange={handleChange}
            placeholder="CIDR Notation, e.g. 8.8.8.8/32"
            type="text"
            isRequired
            error={translate(...(errors.ip_address ?? ''))}
          />
          <Input
            legend={translate('description').toUpperCase()}
            name="description"
            value={form.description ?? ''}
            onChange={handleChange}
            placeholder={translate('description')}
            type="text"
            error={translate(...(errors.description ?? ''))}
          />

          <div className="form-check mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="ip-whitelist-enabled"
              name="is_enabled"
              checked={form.is_enabled}
              onChange={handleEnabledCheckbox}
            />
            <label className="form-check-label" htmlFor="ip-whitelist-enabled">
              {translate('ip_whitelist_status_enable')}
            </label>
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button type="button" className="btn btn-outline-secondary me-2" onClick={onClose}>
              {translate('cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!form.ip_address.trim() || isSubmitting}>
              {isSubmitting ? <Spinner size="sm" /> : translate('save')}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};
